import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { compactMoney, money, percent } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from '@/components/ui/primitives'
import { Table, Td, Th } from '@/components/ui/table'
import type { AnalyticsDashboard } from '@/types/api'

export function Dashboard() {
  const { user } = useAuth()
  const { current } = useCountry()

  const state = useAsync<AnalyticsDashboard>(
    (signal) =>
      api.get<AnalyticsDashboard>('/analytics/dashboard', { countryId: current?.countryId }, signal),
    [current?.countryId],
  )

  const firstName = user?.displayName.split(' ')[0] ?? 'there'

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title={`Good to see you, ${firstName}`}
        hint="Pipeline counts a quotation in the period it was raised. A win counts in the period it closed."
      />

      {state.loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-28" />
          ))}
        </div>
      )}

      {state.error && (
        <ErrorState title={state.error.title} detail={state.error.detail} onRetry={state.reload} />
      )}

      {state.data?.summary.length === 0 && (
        <Card>
          <EmptyState
            title="Nothing to report yet"
            hint="Figures appear once quotations are submitted and closed out. A quotation only counts as won or lost when somebody records the outcome."
          />
        </Card>
      )}

      {/* One block per currency. Ringgit and Singapore dollars are never added together,
          so a multi-country view is several sets of figures rather than one invented total. */}
      {state.data?.summary.map((summary) => (
        <section key={summary.currencyCode} className="mb-8">
          {(state.data?.summary.length ?? 0) > 1 && (
            <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-muted">
              {summary.currencyCode}
            </h2>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="Open pipeline"
              value={compactMoney(summary.pipelineValue, summary.currencySymbol)}
              foot={`${summary.pipelineCount} quotation${summary.pipelineCount === 1 ? '' : 's'}`}
            />
            <Tile
              label="Won"
              value={compactMoney(summary.wonValue, summary.currencySymbol)}
              foot={`${summary.wonCount} closed`}
              tone="pos"
            />
            <Tile
              label="Win rate"
              value={percent(summary.winRateByCount)}
              foot={`${percent(summary.winRateByValue)} by value`}
            />
            <Tile
              label="Average win"
              value={compactMoney(summary.averageWonValue, summary.currencySymbol)}
              foot={`${summary.lostCount} lost`}
            />
          </div>
        </section>
      ))}

      {state.data && state.data.ageing.length > 0 && (
        <Card>
          <CardHeader
            title="How long open quotations have been sitting"
            hint="As of today, whatever period the figures above cover. A quotation nobody answers is the one worth chasing."
          />
          {state.data.ageing.map((report) => (
            <Table key={report.currencyCode}>
              <thead>
                <tr>
                  <Th>Age</Th>
                  <Th numeric>Quotations</Th>
                  <Th numeric>Value ({report.currencySymbol})</Th>
                </tr>
              </thead>
              <tbody>
                {report.buckets.map((bucket) => (
                  <tr key={bucket.label}>
                    <Td>{bucket.label}</Td>
                    <Td numeric>{bucket.count}</Td>
                    <Td numeric>{money(bucket.value, report.currencySymbol, report.currencyDecimals)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ))}
        </Card>
      )}
    </>
  )
}

function Tile({
  label,
  value,
  foot,
  tone,
}: {
  label: string
  value: string
  foot: string
  tone?: 'pos'
}) {
  return (
    <Card className="px-5 py-4">
      <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">{label}</p>
      <p className={`tabular mt-2 text-2xl font-semibold ${tone === 'pos' ? 'text-pos' : ''}`}>
        {value}
      </p>
      <p className="mt-1 text-[12px] text-faint">{foot}</p>
    </Card>
  )
}
